/**
 * CAP Preview — Phase 2 (presentation layer only).
 *
 * Route: /reviewer/cap/:assessmentId/preview
 *
 * Renders a professional, on-screen preview of the Corrective Action Plan
 * that mirrors the visual language of the Reviewer Report (navy/gold/accent
 * theme, helvetica-ish typography via system sans, surface tokens) and
 * serves as the source of truth for the future CAP PDF.
 *
 * Sections:
 *   1) Cover Page
 *   2) Executive Summary (KPI cards)
 *   3) Action Register (grouped by priority)
 *   4) Detailed Action Register (per-finding cards)
 *
 * Data is pulled from the same RPC as the Reviewer CAP tab so ordering and
 * content stay consistent across UI, Preview, CSV and (future) PDF.
 *
 * Strictly presentation. No lifecycle/communications/history changes.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import AppLayout from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer, RefreshCw } from "lucide-react";
import { REPORT_THEME, formatLongDate } from "@/reports/reviewer/theme";
import { displayDomainLabel } from "@/reports/telecom/displayDomainLabel";
import { resolveFindingControlLabel } from "@/lib/findingControlLabel";
import { CAP_STATUS_META, normalizeCapStatus } from "@/components/cap/capStatus";
import { formatDueDate, parseDueDate } from "@/lib/dueDate";

const sb: any = supabase as any;

// ---- Types (mirror reviewer_get_action_plan payload) ----
interface CapFinding {
  id: string;
  display_id?: string | null;
  display_seq?: number | null;
  control_code?: string | null;
  finding_title?: string | null;
  risk_level?: string | null;
  priority_level?: string | null;
  timeframe?: string | null;
  recommended_action?: string | null;
  recommended_measures?: string | null;
  remediation_path?: string | null;
  owner?: string | null;
  owners?: string[] | null;
  due_date?: string | null;
  cap_status?: string | null;
  csv_domain?: string | null;
  nist_function?: string | null;
  source_type?: string | null;
  source_key?: string | null;
  infrastructure_control_key?: string | null;
}

interface CapPayload {
  assessment_id: string;
  version: number;
  published_at: string | null;
  published_by: string | null;
  findings: CapFinding[];
}

interface AssessmentProfile {
  client_organization?: string | null;
  parent_organization?: string | null;
  sector?: string | null;
  region?: string | null;
  methodology?: string | null;
  methodology_frameworks?: string[] | null;
  assessment_conducted_by?: string | null;
}

// ---- Helpers ----
type PriorityKey = "Immediate" | "Quick Win" | "Strategic" | "Other";

const PRIORITY_CHIP: Record<PriorityKey, string> = {
  Immediate: "bg-destructive/10 text-destructive border-destructive/30",
  "Quick Win": "bg-amber-500/10 text-amber-700 border-amber-500/30",
  Strategic: "bg-blue-500/10 text-blue-700 border-blue-500/30",
  Other: "bg-muted text-muted-foreground border-border",
};

function resolvePriorityKey(p: string | null | undefined): PriorityKey {
  const v = (p ?? "").trim();
  if (v === "Immediate" || v === "Quick Win" || v === "Strategic") return v;
  return "Other";
}

function fmtOwners(f: CapFinding): string {
  const arr = (f.owners ?? []).filter(Boolean);
  if (arr.length) return arr.join(", ");
  return (f.owner || "").trim() || "—";
}

function fmtDate(v?: string | null): string {
  return formatDueDate(v) || "—";
}

// ---- Page ----
const CapPreview: React.FC = () => {
  const { assessmentId = "" } = useParams<{ assessmentId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [payload, setPayload] = useState<CapPayload | null>(null);
  const [profile, setProfile] = useState<AssessmentProfile | null>(null);
  const [questionnaireId, setQuestionnaireId] = useState<string>("");
  const [reviewerName, setReviewerName] = useState<string>("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!assessmentId) return;
      setLoading(true);
      setError(null);
      try {
        const [planRes, profileRes, detailRes] = await Promise.all([
          sb.rpc("reviewer_get_action_plan", { p_assessment_id: assessmentId }),
          sb.rpc("reviewer_get_assessment_profile", { p_assessment_id: assessmentId }),
          sb.rpc("reviewer_get_assessment_detail", { p_assessment_id: assessmentId }).then(
            (r: any) => r,
            (e: any) => ({ data: null, error: e }),
          ),
        ]);

        if (planRes.error) throw planRes.error;
        if (cancelled) return;
        setPayload(planRes.data as CapPayload);

        const prof = Array.isArray(profileRes?.data) ? profileRes.data[0] : profileRes?.data;
        setProfile(prof ?? null);

        const detail = Array.isArray(detailRes?.data) ? detailRes.data[0] : detailRes?.data;
        if (detail) {
          setQuestionnaireId(detail.questionnaire_id ?? "");
          setReviewerName(detail.reviewer_name ?? detail.reviewer_full_name ?? "");
        }

        // Fallback: current reviewer's display name
        if (!reviewerName) {
          const { data: userData } = await sb.auth.getUser();
          const meta = userData?.user?.user_metadata ?? {};
          const fallback =
            meta.full_name || meta.name || userData?.user?.email || "";
          if (!cancelled && fallback) setReviewerName(fallback);
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? "Failed to load Corrective Action Plan");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentId]);

  const findings = payload?.findings ?? [];

  // Tallies for KPIs only (priority breakdowns from raw list, not grouped table).
  const priorityCounts = useMemo(() => {
    const c: Record<PriorityKey, number> = { Immediate: 0, "Quick Win": 0, Strategic: 0, Other: 0 };
    for (const f of findings) c[resolvePriorityKey(f.priority_level)]++;
    return c;
  }, [findings]);

  const stats = useMemo(() => {
    const total = findings.length;
    const immediate = priorityCounts.Immediate;
    const quickWin = priorityCounts["Quick Win"];
    const strategic = priorityCounts.Strategic;
    const norm = (f: CapFinding) => normalizeCapStatus(f.cap_status);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const open = findings.filter((f) => norm(f) === "open").length;
    const inProgress = findings.filter((f) => norm(f) === "in_progress").length;
    const completed = findings.filter((f) => norm(f) === "closed").length;
    const overdue = findings.filter((f) => {
      const d = parseDueDate(f.due_date);
      if (!d) return false;
      if (norm(f) === "closed") return false;
      return d < today;
    }).length;
    return { total, immediate, quickWin, strategic, open, inProgress, completed, overdue };
  }, [findings, priorityCounts]);

  const clientName =
    profile?.client_organization || "Client Organization";
  const frameworks =
    (profile?.methodology_frameworks ?? []).filter(Boolean).join(" / ") ||
    profile?.methodology ||
    "—";

  const handlePrint = () => window.print();

  return (
    <AppLayout
      title="Corrective Action Plan — Preview"
      description="Review the published Corrective Action Plan before downloading."
    >
      {/* Toolbar (hidden on print) */}
      <div className="print:hidden flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate(`/reviewer/${assessmentId}`)}
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Reviewer Console
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => window.location.reload()}
            disabled={loading}
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={handlePrint}>
            <Printer className="h-4 w-4 mr-1" /> Print
          </Button>
          {/* Phase 3: Download CAP PDF uses the new professional generator
              that mirrors this preview's structure (cover, KPI summary,
              priority-grouped register, detailed cards). The legacy
              single-table exporter remains available for CSV. */}
          <Button
            size="sm"
            onClick={async () => {
              if (!payload) return;
              const { generateCapReport } = await import(
                "@/reports/actionPlan/generateCapReport"
              );
              generateCapReport(payload as any, {
                clientName,
                parentOrganization: profile?.parent_organization ?? null,
                sector: profile?.sector ?? null,
                assessmentName: questionnaireId,
                questionnaireId,
                frameworks,
                reviewerName,
              });
            }}
            disabled={!payload || findings.length === 0}
          >
            Download CAP PDF
          </Button>
        </div>
      </div>


      {loading && (
        <div className="bg-card border rounded-lg p-8 text-center text-muted-foreground">
          Loading Corrective Action Plan…
        </div>
      )}
      {error && !loading && (
        <div className="bg-destructive/5 border border-destructive/30 text-destructive rounded-lg p-6">
          {error}
        </div>
      )}

      {!loading && !error && payload && (
        <div className="mx-auto max-w-5xl space-y-8 pb-12">
          <DocumentTitle clientName={clientName} />

          <ExecutiveSummary stats={stats} />

          <ActionRegister findings={findings} />
        </div>
      )}
    </AppLayout>
  );
};

export default CapPreview;

// ============================================================
// Document Title + intro (replaces former cover page)
// ============================================================
const CAP_INTRO =
  "This Corrective Action Plan (CAP) translates assessment findings into prioritized remediation activities designed to strengthen cybersecurity maturity and reduce organizational risk. Actions are organized by implementation priority and target completion dates. Immediate Actions and Quick Wins represent mandatory remediation activities requiring management attention, while Strategic Initiatives represent longer-term maturity enhancements that should be incorporated into future cybersecurity planning.";

const DocumentTitle: React.FC<{ clientName: string }> = ({ clientName }) => {
  return (
    <section className="space-y-3">
      <div className="h-[2px] w-14" style={{ background: REPORT_THEME.gold.hex }} />
      {clientName && (
        <p className="text-base md:text-lg font-semibold" style={{ color: "#6e6e6e" }}>
          {clientName}
        </p>
      )}
      <h1 className="text-3xl md:text-4xl font-bold leading-tight" style={{ color: REPORT_THEME.navy.hex }}>
        Corrective Action Plan
      </h1>
      <div className="h-px w-full" style={{ background: REPORT_THEME.border.hex }} />
      <p className="text-sm md:text-[15px] leading-relaxed pt-1" style={{ color: "#1e1e1e" }}>
        {CAP_INTRO}
      </p>
    </section>
  );
};

// ============================================================
// Executive Summary
// ============================================================
const ExecutiveSummary: React.FC<{
  stats: {
    total: number;
    immediate: number;
    quickWin: number;
    strategic: number;
    open: number;
    inProgress: number;
    completed: number;
    overdue: number;
  };
}> = ({ stats }) => {
  const required = stats.immediate + stats.quickWin;
  return (
    <section className="space-y-3">
      <SectionHeader number="01" title="Executive Summary" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Total Actions" value={stats.total} tone="navy" />
        <KpiCard label="Required Actions" value={required} tone="navy" />
        <KpiCard label="Strategic Opportunities" value={stats.strategic} tone="navy" />
        <KpiCard label="Overdue" value={stats.overdue} tone="navy" />
        <KpiCard label="Open" value={stats.open} tone="navy" />
        <KpiCard label="In Progress" value={stats.inProgress} tone="navy" />
        <KpiCard label="Completed" value={stats.completed} tone="navy" />
      </div>
      <p className="text-[12px] italic pt-1" style={{ color: "#6e6e6e" }}>
        Required Actions should be prioritized for remediation within the target implementation period. Strategic Opportunities may be incorporated into future cybersecurity planning cycles.
      </p>
    </section>
  );
};

const KpiCard: React.FC<{
  label: string;
  value: number;
  tone: "navy" | "danger" | "warning" | "info" | "success" | "muted";
}> = ({ label, value, tone }) => {
  const palette: Record<typeof tone, { bar: string; text: string }> = {
    navy: { bar: REPORT_THEME.navy.hex, text: REPORT_THEME.navy.hex },
    danger: { bar: "#b91c1c", text: "#b91c1c" },
    warning: { bar: "#b45309", text: "#b45309" },
    info: { bar: REPORT_THEME.accent.hex, text: REPORT_THEME.accent.hex },
    success: { bar: "#15803d", text: "#15803d" },
    muted: { bar: "#6b7280", text: "#374151" },
  } as const;
  const c = palette[tone];
  return (
    <div
      className="bg-card rounded-lg border p-4 relative overflow-hidden"
      style={{ borderColor: REPORT_THEME.border.hex }}
    >
      <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: c.bar }} />
      <div
        className="text-[10px] font-bold tracking-[0.12em] uppercase"
        style={{ color: "#6e6e6e" }}
      >
        {label}
      </div>
      <div className="text-2xl font-semibold mt-1" style={{ color: c.text }}>
        {value}
      </div>
    </div>
  );
};

// ============================================================
// Action Register — split by Required vs Strategic. Within each section:
// due date ASC, then priority (Immediate < Quick Win < Strategic), then
// display_seq.
// ============================================================
const PRIORITY_ORDER: Record<PriorityKey, number> = {
  Immediate: 0,
  "Quick Win": 1,
  Strategic: 2,
  Other: 3,
};

function sortForSection(items: CapFinding[]): CapFinding[] {
  return [...items].sort((a, b) => {
    const da = parseDueDate(a.due_date)?.getTime() ?? Number.POSITIVE_INFINITY;
    const db = parseDueDate(b.due_date)?.getTime() ?? Number.POSITIVE_INFINITY;
    if (da !== db) return da - db;
    const pa = PRIORITY_ORDER[resolvePriorityKey(a.priority_level)];
    const pb = PRIORITY_ORDER[resolvePriorityKey(b.priority_level)];
    if (pa !== pb) return pa - pb;
    const sa = a.display_seq ?? Number.MAX_SAFE_INTEGER;
    const sb = b.display_seq ?? Number.MAX_SAFE_INTEGER;
    return sa - sb;
  });
}

const SectionIntro: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-[13px] leading-relaxed" style={{ color: "#1e1e1e" }}>
    {children}
  </p>
);

const ActionRegister: React.FC<{ findings: CapFinding[] }> = ({ findings }) => {
  const mandatory = useMemo(
    () =>
      sortForSection(
        findings.filter((f) => {
          const k = resolvePriorityKey(f.priority_level);
          return k === "Immediate" || k === "Quick Win";
        }),
      ),
    [findings],
  );
  const strategic = useMemo(
    () =>
      sortForSection(
        findings.filter((f) => {
          const k = resolvePriorityKey(f.priority_level);
          return k === "Strategic" || k === "Other";
        }),
      ),
    [findings],
  );

  return (
    <>
      <section className="space-y-3">
        <SectionHeader number="02" title="Required Remediation Actions" />
        <SectionIntro>
          These remediation activities should be prioritized within the target implementation period to reduce cybersecurity risk and strengthen the organization's overall security posture.
        </SectionIntro>
        <RegisterTable items={mandatory} emptyText="No required remediation actions identified." />
      </section>

      {strategic.length > 0 && (
        <section className="space-y-3">
          <SectionHeader number="03" title="Strategic Improvement Opportunities" />
          <SectionIntro>
            These initiatives support long-term cybersecurity maturity, resilience, and operational effectiveness. While not typically required for near-term risk reduction, they are recommended for future security program enhancement.
          </SectionIntro>
          <RegisterTable items={strategic} emptyText="No strategic improvement opportunities identified." />
        </section>
      )}
    </>
  );
};

const RegisterTable: React.FC<{ items: CapFinding[]; emptyText: string }> = ({
  items,
  emptyText,
}) => {
  if (items.length === 0) {
    return (
      <div
        className="rounded-lg border bg-card p-6 text-center text-sm text-muted-foreground"
        style={{ borderColor: REPORT_THEME.border.hex }}
      >
        {emptyText}
      </div>
    );
  }
  return (
    <div
      className="rounded-lg border overflow-hidden bg-card"
      style={{ borderColor: REPORT_THEME.border.hex }}
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: REPORT_THEME.surfaceAlt.hex }}>
              <Th>Finding ID</Th>
              <Th>Finding</Th>
              <Th>Required Action</Th>
              <Th>Owner(s)</Th>
              <Th className="w-[110px]">Due Date</Th>
              <Th className="w-[110px]">Priority</Th>
              <Th className="w-[140px]">CAP Status</Th>
            </tr>
          </thead>
          <tbody>
            {items.map((f, idx) => {
              const status = CAP_STATUS_META[normalizeCapStatus(f.cap_status)];
              const pKey = resolvePriorityKey(f.priority_level);
              return (
                <tr
                  key={f.id}
                  style={{
                    background: idx % 2 === 0 ? "#fff" : REPORT_THEME.surfaceAlt.hex,
                  }}
                >
                  <Td>
                    <span
                      className="font-mono text-[11px] font-semibold"
                      style={{ color: REPORT_THEME.accent.hex }}
                    >
                      {f.display_id || "—"}
                    </span>
                  </Td>
                  <Td>
                    <div className="font-medium" style={{ color: REPORT_THEME.navy.hex }}>
                      {f.finding_title || (
                        <span className="italic text-muted-foreground">No title</span>
                      )}
                    </div>
                    {(f.csv_domain || f.source_type) && (
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        {displayDomainLabel(f.csv_domain, f.source_type)}
                      </div>
                    )}
                  </Td>
                  <Td className="text-[13px]">
                    {f.recommended_action ? (
                      <span className="line-clamp-3">{f.recommended_action}</span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </Td>
                  <Td className="text-[13px]">{fmtOwners(f)}</Td>
                  <Td className="text-[13px]">{fmtDate(f.due_date)}</Td>
                  <Td>
                    <span
                      className={`inline-block rounded px-2 py-0.5 text-[11px] font-medium border ${PRIORITY_CHIP[pKey]}`}
                    >
                      {f.priority_level || "—"}
                    </span>
                  </Td>
                  <Td>
                    <span
                      className={`inline-block rounded px-2 py-0.5 text-[11px] font-medium ${status.className}`}
                    >
                      {status.label}
                    </span>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};


// ============================================================
// Shared bits
// ============================================================
const SectionHeader: React.FC<{ number: string; title: string }> = ({ number, title }) => (
  <div className="flex items-baseline gap-3 border-b pb-2" style={{ borderColor: REPORT_THEME.border.hex }}>
    <span
      className="text-[11px] font-bold tracking-[0.15em]"
      style={{ color: REPORT_THEME.gold.hex }}
    >
      SECTION {number}
    </span>
    <h2 className="text-lg font-semibold" style={{ color: REPORT_THEME.navy.hex }}>
      {title}
    </h2>
    <span
      className="ml-auto inline-block h-[2px] w-16"
      style={{ background: REPORT_THEME.gold.hex }}
    />
  </div>
);


const Th: React.FC<React.ThHTMLAttributes<HTMLTableCellElement>> = ({ className = "", children, ...rest }) => (
  <th
    {...rest}
    className={`text-left text-[11px] font-bold tracking-[0.05em] uppercase px-3 py-2 ${className}`}
    style={{ color: REPORT_THEME.navy.hex }}
  >
    {children}
  </th>
);

const Td: React.FC<React.TdHTMLAttributes<HTMLTableCellElement>> = ({ className = "", children, ...rest }) => (
  <td {...rest} className={`align-top px-3 py-2 ${className}`}>
    {children}
  </td>
);
